import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import PhoneNumberInput from 'components/PhoneNumberInput'
import useStates from 'hooks/useStates'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { filterValues } from 'Helper'

export default withTranslation()(function AddBranch({ t, branch, handleClick, onSaved }) {
  const dispatch = useDispatch()
  const [step, setStep] = useState(1)
  const { states } = useStates()
  const { data: contactsRes } = useSWR('merchants/contacts')
  const contacts = contactsRes?.data ?? []

  const method = useForm({ mode: 'all' })
  const { watch, reset, handleSubmit } = method

  useEffect(() => {
    if (branch) {
      reset({
        name: branch.name,
        state_id: branch.state_id,
        town: branch.town,
        address: branch.address,
        landmark: branch.landmark,
        google_map_url: branch.google_map_url,
        phone: branch.phone,
        alternate_phone: branch.alternate_phone,
        email: branch.email,
        branch_manager_id: branch.branch_manager_id,
        contact_person_id: branch.contact_person_id
      })
    }
  }, [branch, reset])

  const [name, state_id, town, address] = watch(['name', 'state_id', 'town', 'address'])
  const [phone, email, branch_manager_id, contact_person_id] = watch(['phone', 'email', 'branch_manager_id', 'contact_person_id'])

  const step1Valid = Boolean(name && state_id && town && address)
  const step2Valid = Boolean(phone && email && branch_manager_id && contact_person_id)

  const stateName = states?.find((state) => `${state.id}` === `${state_id}`)?.name

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      await apiRequest({
        method: branch ? 'put' : 'post',
        url: branch ? `merchants/branches/${branch.id}` : 'merchants/branches',
        payload: { ...payload, location: [payload.town, stateName].filter(Boolean).join(', ') }
      })

      toast.success(t(branch ? 'settings.branch.form.edit_success' : 'settings.branch.form.success'))
      onSaved && onSaved()
      handleClick()
    } catch ({ message }) {
      toast.error(message)
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative w-11/12 max-w-md rounded-xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8 pb-0 shrink-0">
          <div className="relative flex items-center justify-center mb-4">
            <Btn
              className="w-10 h-10 hover:bg-default absolute left-0"
              content={() => <UIcons.UilTimes className="text-secondary" />}
              click={handleClick}
            />

            <h3 className="text-xl font-bold text-dark-blue">{t(branch ? 'settings.branch.edit' : 'settings.branch.add')}</h3>
          </div>

          <div className="w-full h-1.5 bg-border-lines rounded-full mb-6 flex gap-1 overflow-hidden">
            <div className="h-full bg-primary rounded-full flex-1" />
            <div className={`h-full rounded-full flex-1 ${step === 2 ? 'bg-primary' : 'bg-border-lines'}`} />
          </div>
        </div>

        <div className="overflow-y-auto px-6 md:px-8 pb-6 md:pb-8">
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(submit)}>
            {
              step === 1 ? (
                <div key="step-1">
                  <div className="mb-3">
                    <Input
                      name="name"
                      label={t('settings.branch.table.header.name')}
                      placeholder={t('settings.branch.form.name.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="state_id"
                      label={t('settings.branch.form.state_id.label')}
                      placeholder={t('settings.branch.form.state_id.placeholder')}
                      variant="outlined"
                      options={states ? filterValues({ options: states, param: { value: 'id', label: 'name' } }) : []}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="town"
                      label={t('settings.branch.form.town.label')}
                      placeholder={t('settings.branch.form.town.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="address"
                      label={t('settings.branch.form.address.label')}
                      placeholder={t('settings.branch.form.address.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="landmark"
                      label={t('settings.branch.form.landmark.label')}
                      placeholder={t('settings.branch.form.landmark.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="google_map_url"
                      label={t('settings.branch.form.google_map_url.label')}
                      placeholder={t('settings.branch.form.google_map_url.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="flex justify-end mt-5">
                    <Btn
                      content={t('misc.next')}
                      className="h-12 px-6 rounded-xl bg-primary text-white disabled:opacity-40"
                      click={() => setStep(2)}
                      disabled={!step1Valid}
                    />
                  </div>
                </div>
              ) : (
                <div key="step-2">
                  <div className="mb-3">
                    <PhoneNumberInput
                      name="phone"
                      label={t('settings.branch.form.phone.label')}
                      className={{}}
                      singleInputClassName="w-full bg-white rounded-lg py-3"
                      country="NG"
                      code="234"
                    />
                  </div>

                  <div className="mb-3">
                    <PhoneNumberInput
                      name="alternate_phone"
                      label={t('settings.branch.form.alternate_phone.label')}
                      className={{}}
                      singleInputClassName="w-full bg-white rounded-lg py-3"
                      country="NG"
                      code="234"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="email"
                      label={t('settings.branch.form.email.label')}
                      placeholder={t('settings.branch.form.email.placeholder')}
                      variant="outlined"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="branch_manager_id"
                      label={t('settings.branch.form.branch_manager_id.label')}
                      placeholder={t('settings.branch.form.branch_manager_id.placeholder')}
                      variant="outlined"
                      options={filterValues({ options: contacts, param: { value: 'id', label: 'first_name,last_name' } })}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="contact_person_id"
                      label={t('settings.branch.form.contact_person_id.label')}
                      placeholder={t('settings.branch.form.contact_person_id.placeholder')}
                      variant="outlined"
                      options={filterValues({ options: contacts, param: { value: 'id', label: 'first_name,last_name' } })}
                    />
                  </div>

                  <div className="flex justify-between mt-5">
                    <Btn
                      content={() => <><UIcons.UilArrowLeft size={16} className="mr-2" /> {t('misc.back')}</>}
                      className="h-12 px-6 rounded-xl border border-primary text-primary"
                      click={() => setStep(1)}
                      variant="outline"
                      color="#1976D2"
                    />

                    <FormBtn
                      content={t('misc.save')}
                      className="h-12 px-6 rounded-xl bg-primary text-white disabled:opacity-40"
                      disabled={!step2Valid}
                    />
                  </div>
                </div>
              )
            }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
})
